import { Action, createReducer, on } from '@ngrx/store';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';

import * as RolesActions from './roles.actions';
import { IRole } from '@boma/client-shared';

export const ROLES_FEATURE_KEY = 'roles';

export interface RoleState extends EntityState<IRole> {
  selectedId?: string | number; // which Roles record has been selected
  loaded: boolean; // has the Roles list been loaded
  error?: string | null; // last known error (if any)
  isCreatingOrUpdatingRoles: boolean;
  isApplyingRoles: boolean;
  createOrUpdateError?: string;
  loadingTargetPermissions: string | null; // key of the target loading
  loadingEntityRole: string | null; // id of entity loading
  // entityRoles: IRole[];
  // locationRoles: IRole[];
  // vehicleRoles: IRole[];
  assignedAdminRoles: IRole[];
}

export interface RolesPartialState {
  readonly [ROLES_FEATURE_KEY]: RoleState;
}

export const rolesAdapter: EntityAdapter<IRole> = createEntityAdapter<IRole>();

const initialState: RoleState = rolesAdapter.getInitialState({
  // set initial required properties
  loaded: false,
  loadingEntityRole: null, // id of entity loading
  isCreatingOrUpdatingRoles: false,
  isApplyingRoles: false,
  createOrUpdateError: null,
  roles: [],
  loadingTargetPermissions: null,
  permissions: new Map<string, string[]>(),
  assignedAdminRoles: [],
});

const rolesReducer = createReducer(
  initialState,
  on(RolesActions.loadRoles, (state) => ({
    ...state,
    loaded: false,
    error: null,
  })),

  on(RolesActions.loadEntityRolesSuccess, (state, { payload }) => {
    return rolesAdapter.upsertMany(payload, {
      ...state,
      loaded: true,
    });
  }),
  on(RolesActions.loadLocationRolesSuccess, (state, { payload }) => {
    return rolesAdapter.upsertMany(payload, {
      ...state,
      loaded: true,
    });
  }),
  on(RolesActions.loadVehicleRolesSuccess, (state, { payload }) =>
    rolesAdapter.upsertMany(payload, {
      ...state,
      loaded: true,
    })
  ),

  on(RolesActions.loadAssignedAdminRoles, (state) => ({
    ...state,
    loaded: true,
    assignedAdminRoles: [],
  })),
  on(RolesActions.loadAssignedAdminRolesSuccess, (state, { payload }) => ({
    ...state,
    loaded: true,
    assignedAdminRoles: payload,
  })),

  on(RolesActions.applyUserRoles, (state, _) => ({
    ...state,
    isApplyingRoles: true,
  })),
  on(RolesActions.applyAssignedAdminRolesSuccess, (state, { payload }) => ({
    ...state,
    loaded: true,
    isApplyingRoles: false,
    assignedAdminRoles: payload,
  })),
  on(RolesActions.applyAssignedAdminRolesFailure, (state, { error }) => ({
    ...state,
    error,
    isApplyingRoles: false,
  })),

  on(RolesActions.loadRolesFailure, (state, { error }) => ({
    ...state,
    error,
  })),
  on(RolesActions.createEntityRole, (state, payload) => ({
    ...state,
    createOrUpdateError: null,
    isCreatingOrUpdatingRoles: true,
  })),
  on(RolesActions.updateEntityRole, (state, payload) => ({
    ...state,
    createOrUpdateError: null,
    isCreatingOrUpdatingRoles: true,
  })),
  on(RolesActions.modifyEntityRolesSuccess, (state, { payload }) =>
    rolesAdapter.upsertOne(payload, {
      ...state,
      createOrUpdateError: null,
      isCreatingOrUpdatingRoles: false,
    })
  ),
  on(RolesActions.modifyEntityRolesFailure, (state, { error }) => ({
    ...state,
    createOrUpdateError: error,
    isCreatingOrUpdatingRoles: false,
  })),
  on(RolesActions.setSelectedRole, (state, { roleId }) => ({
    ...state,
    selectedId: roleId,
  })),

  on(RolesActions.loadEntityRoleDetails, (state, { id }) => ({
    ...state,
    loadingEntityRole: id,
  })),
  on(RolesActions.loadEntityRoleDetailsSuccess, (state, { role }) =>
    rolesAdapter.upsertOne(role, {
      ...state,
      loaded: true,
      error: '',
      selectedId: role.id,
      loadingEntityRole: null,
    })
  ),
  on(RolesActions.loadEntityRoleDetailsFailure, (state, { error }) => ({
    ...state,
    loadingEntityRole: null,
    error: error,
  }))
);

export function roleReducer(state: RoleState | undefined, action: Action) {
  return rolesReducer(state, action);
}
